import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import {
  ResponsiveContainer,
  AreaChart, Area,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Download, FileDown, RefreshCw, Loader2, TrendingUp, Wrench, ClipboardList,
  ShieldAlert, Sparkles, AlertTriangle, Grid3x3, UserCheck, DollarSign, Activity, Layers,
} from "lucide-react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import PerformancePanel from "@/components/PerformancePanel";
import DiggerLifeCycle from "@/components/DiggerLifeCycle";
import LivePredictions from "@/components/LivePredictions";
import TimeframePicker, { resolveTimeframe, filterInTimeframe } from "@/components/TimeframePicker";
import { useInsightsPrefs } from "@/lib/insightsPrefs";
import InsightsCustomizeModal from "@/components/InsightsCustomizeModal";
import {
  TopFaultsWidget, FleetHealthHeatmap, TechCompletionWidget, CostEstimationWidget,
} from "@/components/InsightsWidgets";
import {
  CommandKpiStrip, AlertBanner, FilterBar, RepeatFaultWidget,
  OutstandingWorkStrip, ComplianceWidget, WearTrackerWidget,
  AssetHealthDrawer, KpiDrawer,
} from "@/components/InsightsCommand";
import { applyFilters } from "@/lib/insightsIntel";
import { useNavigate } from "react-router-dom";

const ORANGE = "#f58220";
const ORANGE_2 = "#ff9330";
const NAVY = "#3b82f6";
const RED = "#ef4444";
const GREEN = "#22c55e";
const AMBER = "#f59e0b";

export default function Insights() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [assetsList, setAssetsList] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [timeframeId, setTimeframeId] = useState("month");
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [drillAsset, setDrillAsset] = useState(null);
  const [kpiDrawer, setKpiDrawer] = useState(null);
  const tf = resolveTimeframe(timeframeId);
  const DAYS = tf.days;
  const reportRef = useRef(null);

  // Widget catalog — the source of truth for what can appear on this page.
  // Every visible block wraps in <WidgetBlock id="…"> and asks `prefs.isOn`.
  // Josh (Feb 9): "make it so with insight there are heaps of different
  // useful things so they can see but they can also choose what they want
  // to see". Order in this array = default rendering order.
  const WIDGETS = useMemo(() => ([
    { id: "summary",       title: "Summary tiles",          category: "Overview",   icon: Layers,        description: "The four hero counters at the top — Handovers, Breakdowns, Inspections and Hazards." },
    { id: "predictions",   title: "Live predictions",        category: "Overview",   icon: Sparkles,      description: "Next teeth change and other predictive maintenance callouts." },
    { id: "kpis",          title: "Fleet KPIs",              category: "Overview",   icon: TrendingUp,    description: "Availability, MTTR, backlog and PM compliance panels." },
    { id: "activity",      title: "Daily activity",          category: "Trends",     icon: Activity,      description: "Stacked area chart of handovers, breakdowns and inspections over time." },
    { id: "downtime",      title: "Equipment downtime",      category: "Fleet",      icon: Wrench,        description: "Which assets have racked up the most breakdown events in this window." },
    { id: "status-mix",    title: "Breakdown status mix",    category: "Fleet",      icon: ClipboardList, description: "Where every breakdown currently sits (open, in-progress, approved…)." },
    { id: "top-faults",    title: "Top 5 faults",            category: "Fleet",      icon: AlertTriangle, description: "The most frequent breakdown causes across the fleet." },
    { id: "fleet-health",  title: "Fleet Health Heatmap",    category: "Fleet",      icon: Grid3x3,       description: "Every asset colour-coded by live status — Ready / Working / At Risk / Down." },
    { id: "tech-completion", title: "Technician completion", category: "People",     icon: UserCheck,     description: "Inspections finished vs. still open per crew member — spot bottlenecks fast." },
    { id: "cost-estimate", title: "Cost estimation",         category: "Financial",  icon: DollarSign,    description: "Rough parts + labour spend from Planning Request trade-hour data." },
    { id: "life-cycle",    title: "Digger Life Cycle",       category: "Fleet",      icon: Layers,        description: "Where the fleet sits across the New → Replace curve." },
  ]), []);
  const prefs = useInsightsPrefs(WIDGETS.map((w) => w.id));

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const [hRes, bRes, iRes, hzRes, pRes, aRes] = await Promise.allSettled([
        api.get("/handovers"),
        api.get("/breakdowns"),
        api.get("/inspections"),
        api.get("/hazards/pending"),
        api.get("/ppfs"),
        api.get("/assets"),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      setData({
        handovers: list(hRes),
        breakdowns: list(bRes),
        inspections: list(iRes),
        hazards: list(hzRes),
        ppfs: list(pRes),
      });
      setAssetsList(list(aRes));
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Derived series -----------------------------------------------------
  const bucketByDay = (items, dateField = "created_at") => {
    const buckets = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Cap the number of X-axis points so long ranges (FY, 365d) stay readable.
    const barCount = Math.min(DAYS, 60);
    const bucketSizeDays = Math.max(1, Math.ceil(DAYS / barCount));
    for (let i = barCount - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i * bucketSizeDays);
      buckets.set(todayLocalIso(d), 0);
    }
    const keys = [...buckets.keys()];
    (items || []).forEach((it) => {
      const raw = it[dateField] || it.created_at || it.reported_at || it.ts || it.date;
      if (!raw) return;
      const t = new Date(raw).getTime();
      if (isNaN(t)) return;
      // Snap to the nearest bucket key that isn't in the future.
      const key = new Date(t);
      key.setHours(0, 0, 0, 0);
      const iso = todayLocalIso(key);
      // Find nearest bucket <= this date.
      let matched = null;
      for (const k of keys) if (k <= iso) matched = k;
      if (matched) buckets.set(matched, buckets.get(matched) + 1);
    });
    return [...buckets.entries()].map(([date, value]) => ({
      date: bucketSizeDays > 1 ? date.slice(5) : date.slice(5),
      value,
    }));
  };

  const priorityPie = (items) => {
    const counts = { low: 0, medium: 0, high: 0, critical: 0, unknown: 0 };
    (items || []).forEach((it) => {
      const p = String(it.priority || it.severity || "unknown").toLowerCase();
      if (counts[p] != null) counts[p]++; else counts.unknown++;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  };

  const downtimeByAsset = (items) => {
    // Estimate: count breakdowns per asset (proxy for downtime events)
    const map = new Map();
    (items || []).forEach((it) => {
      const key = it.asset_id || it.asset_name || it.asset || "Unassigned";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([asset, count]) => ({ asset: String(asset).slice(0, 22), count }));
  };

  const statusMix = (items) => {
    const map = new Map();
    (items || []).forEach((it) => {
      const key = String(it.status || it.stage || "unknown").toLowerCase();
      map.set(key, (map.get(key) || 0) + 1);
    });
    return [...map.entries()].map(([name, value]) => ({ name, value }));
  };

  const PIE_COLORS = { critical: RED, high: ORANGE, medium: NAVY, low: GREEN, unknown: "#555" };
  const STATUS_COLORS = [ORANGE, ORANGE_2, NAVY, GREEN, AMBER, RED, "#a78bfa", "#94a3b8"];

  // Export -------------------------------------------------------------
  const exportPng = async () => {
    if (!reportRef.current) return;
    const dataUrl = await toPng(reportRef.current, {
      backgroundColor: "#0a0a0a",
      pixelRatio: 2,
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `pit-link-insights-${todayLocalIso()}.png`;
    a.click();
  };

  const exportPdf = async () => {
    if (!reportRef.current) return;
    const dataUrl = await toPng(reportRef.current, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
    });
    // A4 landscape at 72dpi = 842x595 pt. We'll fit to width.
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const img = new Image();
    img.src = dataUrl;
    await new Promise((r) => (img.onload = r));
    const ratio = img.width / img.height;
    let w = pageW - 40;
    let h = w / ratio;
    if (h > pageH - 40) { h = pageH - 40; w = h * ratio; }
    pdf.addImage(dataUrl, "PNG", (pageW - w) / 2, 20, w, h);
    pdf.save(`pit-link-insights-${todayLocalIso()}.pdf`);
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-3 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading insights…
      </div>
    );
  }

  // Apply the selected timeframe to each dataset before charting.
  const filteredHandovers = filterInTimeframe(data.handovers, tf);
  const filteredBreakdowns = filterInTimeframe(data.breakdowns, tf);
  const filteredInspections = filterInTimeframe(data.inspections, tf);
  const filteredHazards = data.hazards; // hazards pending is a live snapshot

  const dailyHandovers = bucketByDay(filteredHandovers);
  const dailyBreakdowns = bucketByDay(filteredBreakdowns);
  const dailyInspections = bucketByDay(filteredInspections);
  const merged = dailyHandovers.map((h, i) => ({
    date: h.date,
    handovers: h.value,
    breakdowns: dailyBreakdowns[i]?.value || 0,
    inspections: dailyInspections[i]?.value || 0,
  }));

  const breakdownPriorities = priorityPie(filteredBreakdowns);
  const breakdownStatus = statusMix(filteredBreakdowns);
  const worstAssets = downtimeByAsset(filteredBreakdowns);

  return (
    <div className="space-y-8" data-testid="insights-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 no-print">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            Performance · Insights
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">
            Performance
          </h1>
          <p className="text-muted mt-2 max-w-xl">
            {tf.longLabel} view across handovers, breakdowns, inspections and hazards — pulled live from the Pit Link backend.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <TimeframePicker value={timeframeId} onChange={setTimeframeId} testid="insights-timeframe" />
          <button
            className="btn btn-sm"
            onClick={() => setCustomizeOpen(true)}
            data-testid="btn-customize"
            title="Choose which widgets appear on this dashboard"
          >
            <Sparkles className="w-4 h-4" /> Customize
          </button>
          <button className="btn btn-sm btn-ghost" onClick={load} disabled={busy} data-testid="btn-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button className="btn btn-sm" onClick={exportPng} data-testid="btn-export-png">
            <Download className="w-4 h-4" /> PNG
          </button>
          <button className="btn btn-sm btn-primary" onClick={exportPdf} data-testid="btn-export-pdf">
            <FileDown className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2.5 !px-4 justify-start">{err}</div>
      )}

      {/* ==== NEW: Command Dashboard (Josh Feb 22 spec) ==================
          Alerts → KPIs → Filters → Repeat Faults → Outstanding → Compliance → Wear
          Filters apply their scope to everything BELOW this block (raw data
          filtered via `applyFilters`). Old Recharts widgets keep their own
          timeframe filter, so this is additive and cannot regress them.
          Clickable throughout — plain click drills into a drawer, Shift/Cmd
          click jumps to the filtered list page. */}
      <AlertBanner
        data={data}
        assets={assetsList}
        onDrillAsset={setDrillAsset}
        onDrillRecord={(d) => d?.asset_id && setDrillAsset(d.asset_id)}
      />
      <CommandKpiStrip
        data={data}
        assets={assetsList}
        onDrill={(payload) => setKpiDrawer(payload)}
      />
      <FilterBar
        data={data}
        assets={assetsList}
        filters={filters}
        onChange={setFilters}
      />
      <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))" }}>
        <RepeatFaultWidget
          breakdowns={applyFilters(data, filters).breakdowns}
          onDrillAsset={setDrillAsset}
        />
        <WearTrackerWidget
          assets={assetsList}
          onDrillAsset={setDrillAsset}
        />
      </div>
      <OutstandingWorkStrip data={applyFilters(data, filters)} onNav={nav} />
      <div className="mb-6">
        <ComplianceWidget
          inspections={applyFilters(data, filters).inspections}
          onNav={nav}
        />
      </div>

      {/* THIS IS THE EXPORTED REGION */}
      <div ref={reportRef} className="space-y-6" data-testid="report-region">
        {/* Header row shown in exported doc */}
        <div className="panel-heavy p-6 flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-black" style={{ color: ORANGE }}>
              Pit Link · Performance Report
            </div>
            <div className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1">
              {tf.longLabel} snapshot
            </div>
            <div className="text-xs text-muted mt-1 font-mono-tight">
              Generated {new Date().toLocaleString()} · {tf.start.toLocaleDateString()} → {tf.end.toLocaleDateString()}
            </div>
          </div>
          <img src="/shiftlog-logo.png?v=pl2" alt="Pit Link"
               style={{ height: 60, filter: "brightness(1.35) contrast(1.05)" }} />
        </div>

        {/* Summary tiles */}
        {prefs.isOn("summary") && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryTile label={`Handovers (${tf.label})`} value={filteredHandovers.length} icon={ClipboardList} />
            <SummaryTile label={`Breakdowns (${tf.label})`} value={filteredBreakdowns.length} icon={Wrench} tone="hazard" />
            <SummaryTile label={`Inspections (${tf.label})`} value={filteredInspections.length} icon={TrendingUp} />
            <SummaryTile label={`Hazards Pending`} value={filteredHazards.length} icon={ShieldAlert} tone={filteredHazards.length ? "danger" : undefined} />
          </div>
        )}

        {/* Live Predictions — Next Teeth Change */}
        {prefs.isOn("predictions") && <LivePredictions />}

        {/* Fleet KPIs · timeframe-aware */}
        {prefs.isOn("kpis") && <PerformancePanel timeframe={tf} />}

        {/* Activity trend */}
        {prefs.isOn("activity") && (
          <ChartCard title="Daily activity" subtitle="Handovers · Breakdowns · Inspections">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={merged} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="hOrange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ORANGE} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={ORANGE} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="hNavy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NAVY} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="hGreen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GREEN} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="handovers" stroke={GREEN} fill="url(#hGreen)" strokeWidth={2} />
                <Area type="monotone" dataKey="breakdowns" stroke={ORANGE} fill="url(#hOrange)" strokeWidth={2.5} />
                <Area type="monotone" dataKey="inspections" stroke={NAVY} fill="url(#hNavy)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {(prefs.isOn("downtime") || prefs.isOn("status-mix")) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {prefs.isOn("downtime") && (
              <ChartCard title="Equipment downtime" subtitle={`Breakdown events · top ${worstAssets.length}`}>
                {worstAssets.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={worstAssets} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} layout="vertical">
                      <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="asset" type="category" width={140} />
                      <Tooltip />
                      <Bar dataKey="count" fill={ORANGE} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </ChartCard>
            )}

            {prefs.isOn("status-mix") && (
              <ChartCard title="Breakdown status mix" subtitle="Where things sit right now">
                {breakdownStatus.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={breakdownStatus} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {breakdownStatus.map((_, i) => (
                          <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </ChartCard>
            )}
          </div>
        )}

        {/* NEW WIDGETS — added Feb 9 per Josh's Insights hub ask */}
        {prefs.isOn("top-faults") && (
          <ChartCard title="Top 5 faults" subtitle="Most frequent breakdown causes in this window">
            <TopFaultsWidget breakdowns={filteredBreakdowns} />
          </ChartCard>
        )}

        {prefs.isOn("fleet-health") && (
          <ChartCard title="Fleet Health Heatmap" subtitle="Every asset · live status">
            <FleetHealthHeatmap inspections={data.inspections} breakdowns={data.breakdowns} />
          </ChartCard>
        )}

        {prefs.isOn("tech-completion") && (
          <ChartCard title="Technician completion" subtitle="Inspections finished vs. still open per crew member">
            <TechCompletionWidget inspections={filteredInspections} />
          </ChartCard>
        )}

        {prefs.isOn("cost-estimate") && (
          <ChartCard title="Cost estimation" subtitle="Rough parts + labour spend from Planning Request trade-hour data">
            <CostEstimationWidget ppfs={filterInTimeframe(data.ppfs, tf)} />
          </ChartCard>
        )}

        {prefs.isOn("life-cycle") && (
          <ChartCard title="Digger Life Cycle" subtitle="Fleet position across the New → Replace cycle">
            <DiggerLifeCycle />
          </ChartCard>
        )}

        <div className="text-center text-[10px] uppercase tracking-widest text-muted font-mono-tight pt-2">
          pit-link · field-maint.emergent.host · confidential
        </div>
      </div>

      {customizeOpen && (
        <InsightsCustomizeModal
          widgets={WIDGETS}
          prefs={prefs}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {/* Drill-in drawers — powered by the new command dashboard */}
      <KpiDrawer
        open={kpiDrawer}
        onClose={() => setKpiDrawer(null)}
        onDrillAsset={(id) => { setKpiDrawer(null); setDrillAsset(id); }}
      />
      <AssetHealthDrawer
        assetId={drillAsset}
        data={data}
        assets={assetsList}
        onClose={() => setDrillAsset(null)}
        onNav={nav}
      />
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="panel-heavy p-5 md:p-6">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="font-display font-black uppercase tracking-widest text-sm">{title}</div>
          {subtitle && (
            <div className="text-[11px] uppercase tracking-widest text-muted mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, tone }) {
  const styles =
    tone === "hazard"
      ? { background: "linear-gradient(180deg, #ffb15c, #f58220)", color: "#0a0a0a", borderColor: "#f58220" }
      : tone === "danger"
      ? { background: "linear-gradient(180deg, #ef4444, #b91c1c)", color: "#fff", borderColor: "#ef4444" }
      : {};
  const iconStyle =
    tone === "hazard"
      ? { background: "#0a0a0a", color: "#f58220" }
      : tone === "danger"
      ? { background: "#fff", color: "#b91c1c" }
      : { background: "rgba(245,130,32,0.15)", color: "#f58220", border: "1px solid rgba(245,130,32,0.3)" };
  return (
    <div className="panel-heavy p-5 flex flex-col justify-between min-h-[140px]" style={styles}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black">{label}</div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={iconStyle}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="font-display font-black text-5xl leading-none tracking-tight">
        {value ?? "—"}
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[220px] flex items-center justify-center text-muted text-sm">
      Not enough data yet.
    </div>
  );
}
